import React from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Paper, Typography } from '@mui/material';
import { ClientDetailsV2Header } from '@/clients/details/v2/components/ClientDetailsV2Header';
import { ClientDetailsV2MealPlanTab } from '@/clients/details/v2/components/ClientDetailsV2MealPlanTab';
import { buildClientDetailsV2ViewModel } from '@/clients/details/v2/components/clientDetailsV2.utils';
import { useClientDetails } from '@/hooks/useClientDetails';
import { updateClient } from '@/lib/apiService';
import { mapMedicalConditionsToApiValues } from '@/utils/client-medical-conditions';
import { toFoodIntoleranceApiLabels } from '@/utils/food-intolerances';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { ClientNameLink } from '@/components/clients/ClientNameLink';

const MealPlanningClientMealPlanPage: React.FC = () => {
  const params = useParams();
  const clientId = params.id || '';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { client, isLoading, refreshClientData } = useClientDetails(clientId);
  const viewModel = client ? buildClientDetailsV2ViewModel(client, '') : null;
  const isViewingMealPlan = Boolean(searchParams.get('mealPlanId')?.trim());
  const clientDisplayName = client
    ? client.fullName?.trim() || `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.email || 'Client'
    : '';

  const goToMealPlanning = React.useCallback(() => {
    navigate('/meal-planning');
  }, [navigate]);

  const handleUpdateAllergies = React.useCallback(async (allergies: string[]) => {
    try {
      await updateClient(clientId, { allergies });
      showSuccessToast('Success', 'Allergies updated successfully');
      refreshClientData();
    } catch (err) {
      console.error('Failed to update allergies:', err);
      showErrorToast(err, 'Failed to update allergies');
    }
  }, [clientId, refreshClientData]);

  const handleUpdateIntolerances = React.useCallback(async (intolerances: string[]) => {
    try {
      await updateClient(clientId, { intolerances: toFoodIntoleranceApiLabels(intolerances) });
      showSuccessToast('Success', 'Intolerances updated successfully');
      refreshClientData();
    } catch (err) {
      console.error('Failed to update intolerances:', err);
      showErrorToast(err, 'Failed to update intolerances');
    }
  }, [clientId, refreshClientData]);

  const handleUpdateMedicalConditions = React.useCallback(async (
    medicalConditions: string[],
    medicalConditionSubtypes?: Record<string, string>,
  ) => {
    try {
      await updateClient(clientId, {
        medicalConditions: mapMedicalConditionsToApiValues(medicalConditions),
        medicalConditionSubtypes,
      });
      showSuccessToast('Success', 'Medical conditions updated successfully');
      refreshClientData();
    } catch (err) {
      console.error('Failed to update medical conditions:', err);
      showErrorToast(err, 'Failed to update medical conditions');
    }
  }, [clientId, refreshClientData]);

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.5, lg: 3 }, pt: { xs: 2, sm: 2.5, lg: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: '16px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fafcfe',
              px: { xs: 2, sm: 3 },
              py: 1.5,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link
                component={RouterLink}
                to="/meal-planning"
                underline="none"
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}
              >
                Meal Planning
              </Link>
            </Breadcrumbs>
          </Paper>

          {isViewingMealPlan && viewModel ? (
            <Box sx={{ mb: 3 }}>
              <ClientDetailsV2Header
                clientId={clientId}
                client={client}
                refreshClientData={refreshClientData}
                clientDisplayName={viewModel.clientDisplayName}
                emailDisplay={viewModel.emailDisplay}
                aboutDetails={viewModel.aboutDetails}
                allergies={viewModel.allergies}
                intolerances={viewModel.intolerances}
                conditionNames={viewModel.conditionNames}
                summaryItems={viewModel.summaryItems}
                metrics={viewModel.metrics}
                anthropometricItems={viewModel.anthropometricItems}
                activityAndGoalItems={viewModel.activityAndGoalItems}
                symptomItems={viewModel.symptomItems}
                dietaryPreferencesSummary={viewModel.dietaryPreferencesSummary}
                clientGoalSummary={viewModel.clientGoalSummary}
                nutrientGuidelinesSummary={viewModel.nutrientGuidelinesSummary}
                clientGoalRows={viewModel.clientGoalRows}
                completedQuestionnaires={viewModel.completedQuestionnaires}
                questionnaireCompletedLabel={viewModel.questionnaireCompletedLabel}
                nextAppointmentDate={viewModel.nextAppointmentDate}
                statusLabel={viewModel.statusLabel}
                activeTab={0}
                tabs={[]}
                onTabChange={() => undefined}
                onOpenQuestionnaires={() => navigate(`/clients/${clientId}/details-v2/questionnaires`)}
                onUpdateAllergies={handleUpdateAllergies}
                onUpdateIntolerances={handleUpdateIntolerances}
                onUpdateMedicalConditions={handleUpdateMedicalConditions}
                hideTabs
              />
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '32px',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: '#1D2B2D',
                  textTransform: 'capitalize',
                }}
                component="h1"
              >
                Meal Planning
              </Typography>
              <Typography
                sx={{
                  mt: '2px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: '#717182',
                }}
              >
                {isLoading ? (
                  'Loading client meal plans...'
                ) : clientDisplayName ? (
                  <>
                    Manage meal plans for{' '}
                    <ClientNameLink clientId={clientId} className="text-[#717182] hover:text-[#4b5563]">
                      {clientDisplayName}
                    </ClientNameLink>
                  </>
                ) : (
                  'Manage client meal plans'
                )}
              </Typography>
            </Box>
          )}

          <ClientDetailsV2MealPlanTab clientId={clientId} client={client} onMealPlanBack={goToMealPlanning} />
        </Box>
      </Paper>
    </Box>
  );
};

export default MealPlanningClientMealPlanPage;
